import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
} from "@reduxjs/toolkit";

import { fetchCaseInfo } from "../../../thunks/Case";
import { BUSINESS_PRODUCTS, CreditAccount, CreditAccountProduct, CreditFacility } from "../../../api/credit_accounts";
import { AccountOverview } from "../../../api/account_overviews";
import { LoanProductInfo } from "../../../api/cases";

import {
  fetchAccountOverviews,
  updateCreditAccountDetails,
  upsertCreditAccountCurrentPosition,
  upsertCreditAccountProvision,
  upsertCreditAccountRecovery,
} from "../../accountDetails/logic/accountDetailsThunk";

import {
  addCreditAccounts,
  updateCreditAccount,
} from "./thunks";

import { RootState } from "../../../app/store";
import { selectLoanProductInfo } from "../../caseinfo/logic/Slice";
import { isCreditAccountStakeholdersRevampEnabled } from "../../configs/logic/Slice";
import {
  FacilityCategory,
  getFacilityCategoryForCreditFacility,
} from "./creditAccountMappings";

export interface CreditAccountsState {
  accounts: EntityState<CreditAccount, string>;
  accountOverviews: EntityState<AccountOverview, string>;
  caseId: string | null;
}

export interface AccountProductInfo extends LoanProductInfo {
  account_name?: string;
  credit_facility?: CreditFacility;
  ext_account_number: string;
}

export const isBusinessProduct = (product?: CreditAccountProduct): boolean =>
  !!product && BUSINESS_PRODUCTS.includes(product);

export const creditAccountsAdapter = createEntityAdapter<
  CreditAccount,
  string
>({
  selectId: (account) => account.account_number,
});

export const accountOverviewsAdapter = createEntityAdapter<
  AccountOverview,
  string
>({
  selectId: (accountOverview) => accountOverview.account_number,
});

const initialState: CreditAccountsState = {
  accounts: creditAccountsAdapter.getInitialState(),
  accountOverviews: accountOverviewsAdapter.getInitialState(),
  caseId: null,
};

const creditAccountsSlice = createSlice({
  name: "creditAccounts",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCaseInfo.pending, (state) => {
        creditAccountsAdapter.removeAll(state.accounts);
      })
      .addCase(fetchCaseInfo.fulfilled, (state, action) => {
        const caseData = action.payload.cases?.[0];
                if(caseData && caseData.credit_accounts) {
                    creditAccountsAdapter.setAll(state.accounts, caseData.credit_accounts);
        }
      })
      .addCase(addCreditAccounts.fulfilled, (state, action) => {
                creditAccountsAdapter.addMany(state.accounts, action.payload.credit_accounts);
      })
      .addCase(updateCreditAccount.fulfilled, (state, action) => {
        creditAccountsAdapter.updateOne(state.accounts, {
          id: action.meta.arg.account_number,
          changes: action.meta.arg.request,
        });
      })
      .addCase(fetchAccountOverviews.pending, (state) => {
        accountOverviewsAdapter.removeAll(state.accountOverviews);
      })
      .addCase(fetchAccountOverviews.fulfilled, (state, action) => {
        state.caseId = action.meta.arg;

        accountOverviewsAdapter.setAll(state.accountOverviews,action.payload.account_overviews);
      })
      .addCase(updateCreditAccountDetails.fulfilled, (state, action) => {
          const { accountNumber, request } = action.meta.arg;

          const existingAccountOverview = state.accountOverviews.entities[accountNumber];

          if (!existingAccountOverview) return;

          accountOverviewsAdapter.updateOne(state.accountOverviews, {
            id: accountNumber,
            changes: {
              credit_account_details: {
                ...(existingAccountOverview.credit_account_details ?? {
                  account_number: accountNumber,
                }),
                ...request,
              },
            },
          });
        }
      )
      .addCase( upsertCreditAccountCurrentPosition.fulfilled, (state, action) => {
          const { accountNumber, request } = action.meta.arg;

          const existingAccountOverview = state.accountOverviews.entities[accountNumber];

          if (!existingAccountOverview) return;

          accountOverviewsAdapter.updateOne(state.accountOverviews, {
            id: accountNumber,
            changes: {
              current_position: {
                ...(existingAccountOverview.current_position ?? {
                  account_number: accountNumber,
                }),
                ...request,
              },
            },
          });
        }
      )
      .addCase(upsertCreditAccountProvision.fulfilled, (state, action) => {
          const { accountNumber, request } = action.meta.arg;

          const existingAccountOverview = state.accountOverviews.entities[accountNumber];

          if (!existingAccountOverview) return

          accountOverviewsAdapter.updateOne(state.accountOverviews, {
            id: accountNumber,
            changes: {
              provision_details: {
                ...(existingAccountOverview.provision_details ?? {
                  account_number: accountNumber,
                }),
                ...request,
              },
            },
          });
        }
      )
      .addCase(upsertCreditAccountRecovery.fulfilled, (state, action) => {
          const { accountNumber, request } = action.meta.arg;

          const existingAccountOverview = state.accountOverviews.entities[accountNumber];

          if (!existingAccountOverview) return;

          accountOverviewsAdapter.updateOne(state.accountOverviews, {
            id: accountNumber,
            changes: {
              recovery_details: {
                ...(existingAccountOverview.recovery_details ?? {
                  account_number: accountNumber,
                }),
                ...request,
              },
            },
          });
        }
      );
  },
});

export const creditAccountsReducer = creditAccountsSlice.reducer;

const creditAccountsSelectors = creditAccountsAdapter.getSelectors<RootState>(
    (state) => state.creditAccounts.accounts,
);

export const {
    selectAll: getCreditAccounts,
    selectById: getCreditAccountByAccountNumber,
    selectEntities: getCreditAccountEntities,
} = creditAccountsSelectors;

export const getAccountProductInfo = createSelector(
  [
    isCreditAccountStakeholdersRevampEnabled,
    selectLoanProductInfo,
    getCreditAccounts,
  ],
  (isRevampEnabled, laonProductInfo, creditAccounts): AccountProductInfo[] => {
        if(isRevampEnabled) {
        return creditAccounts.map((account) => ({
                  loan_reference_id: account.account_number,
                  ext_account_number: account.ext_account_number,
                  product: account.product as string,
                  account_name: account.account_name,
                  credit_facility: account.credit_facility,
        }));
    } else {
      return laonProductInfo.map((info) => ({
        ...info,
        ext_account_number: info.loan_reference_id,
      }));
    }
  }
);

export const getAccountProductInfoByAccountNumber = createSelector(
  [
    getAccountProductInfo,
    (_: RootState, accountNumber: string) => accountNumber,
  ],
  (accounts, accountNumber) =>
    accounts.find((account) => account.loan_reference_id === accountNumber),
);

export const getAccountProductInfoByAccountNumberMap = createSelector(
  [getAccountProductInfo],
  (accounts): Record<string, AccountProductInfo> =>
    Object.fromEntries(
      accounts.map((account) => [account.loan_reference_id, account]),
    ),
);

export const { selectAll: getAccountOverviews, selectById: getAccountOverviewByAccountNumber } =
  accountOverviewsAdapter.getSelectors(
    (state: RootState) => state.creditAccounts.accountOverviews
  );

export const getAccountDetailByAccountNumber = createSelector(
  [getCreditAccountByAccountNumber, getAccountOverviewByAccountNumber],
  (account, accountOverview) => ({
    account,
    accountOverview,
  })
)


export const getCreditAccountsByFacilityCategory = createSelector(
  [
    getCreditAccounts,
    (_: RootState, category: FacilityCategory) => category,
  ],
  (accounts, category) =>
    accounts.filter(
      (account) =>
        getFacilityCategoryForCreditFacility(account.credit_facility) === category,
    ),
);